import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { IArea } from '@newroom-connect/library/interfaces';

import { LoggingService } from '../../logging/logging.service';
import { CacheWebSocketMessage, WebSocketMessageHandler } from '../models/cache-models';
import { AreaCacheStore } from '../stores/area-cache-store';
import { CacheStatusService } from '../services/cache-status.service';

/**
 * Handler for WebSocket messages related to areas.
 */
@Injectable({
  providedIn: 'root'
})
export class AreaMessageHandler implements WebSocketMessageHandler<IArea> {
  private readonly logger = inject(LoggingService);
  private readonly areaCacheStore = inject(AreaCacheStore);
  private readonly statusService = inject(CacheStatusService);

  /**
   * Check if this handler can process a given WebSocket message.
   *
   * @param message The WebSocket message to check.
   * @returns True if this handler can process the message.
   */
  public canHandle(message: CacheWebSocketMessage<IArea>): boolean {
    // Handle any message with a topic that contains 'areas'
    return message.topic.includes('areas') && !!message.data;
  }

  /**
   * Handle an area-related WebSocket message.
   *
   * @param message The WebSocket message to handle.
   *
   * @returns Observable that completes when handling is done.
   */
  public handle(message: CacheWebSocketMessage<IArea>): Observable<IArea | boolean | undefined> {
    const { data: area, action, projectId } = message;

    this.logger.debug(`Received area WebSocket message with action ${action} and projectId ${projectId} and area ${area?.id}`);

    if (!area) {
      this.logger.warn('Received area WebSocket message with no data');
      return of(void 0);
    }

    // Update status to show we're processing the message
    this.statusService.updateStatus({
      isLoading: true,
      progress: 0,
      storeId: 'areas',
      itemId: area.id,
      projectId: projectId || area.projectId,
      operation: 'websocket-update'
    });

    // Handle based on the action type
    if (action === 'delete') {
      return this.handleDeleteAction(area, projectId);
    }

    return this.handleUpdateAction(area);
  }

  /**
   * Handle an area update WebSocket message.
   *
   * @param area The area data from the message.
   * @returns Observable that completes when handling is done.
   */
  private handleUpdateAction(area: IArea): Observable<IArea | undefined> {
    this.logger.debug(`Handling area update WebSocket message for area ${area.id}`);

    // Add timestamp to area before caching
    const areaWithTimestamp = {
      ...area,
      timestamp: Date.now()
    };

    // Store the updated area in cache
    return this.areaCacheStore.set(areaWithTimestamp, 'websocket').pipe(
      tap(() => {
        // Update status to show we're done
        this.statusService.updateStatus({
          isLoading: false,
          progress: 100,
          storeId: 'areas',
          itemId: area.id,
          projectId: area.projectId,
          operation: 'websocket-update'
        });
      }),
      catchError(error => {
        this.logger.error(`Error handling area update for ${area.id}:`, error);

        // Update status on error
        this.statusService.updateStatus({
          isLoading: false,
          progress: 100,
          storeId: 'areas',
          itemId: area.id,
          projectId: area.projectId,
          operation: 'websocket-update'
        });

        return of(void 0);
      })
    );
  }

  /**
   * Handle an area delete WebSocket message.
   *
   * @param area The area data from the message.
   * @param projectId Optional project ID from the message.
   *
   * @returns Observable that completes when handling is done.
   */
  private handleDeleteAction(area: IArea, projectId?: string): Observable<boolean | undefined> {
    const areaId = area.id;
    const effectiveProjectId = projectId || area.projectId;

    this.logger.debug(`Handling area delete WebSocket message for area ${areaId}`);

    // Remove the area from cache
    return this.areaCacheStore.remove(areaId, effectiveProjectId, 'websocket').pipe(
      tap(success => {
        if (success) {
          this.logger.debug(`Successfully removed area ${areaId} from cache`);
        } else {
          this.logger.warn(`Failed to remove area ${areaId} from cache`);
        }

        // Update status to show we're done
        this.statusService.updateStatus({
          isLoading: false,
          progress: 100,
          storeId: 'areas',
          itemId: areaId,
          projectId: effectiveProjectId,
          operation: 'websocket-update'
        });
      }),
      catchError(error => {
        this.logger.error(`Error handling area delete for ${areaId}:`, error);

        // Update status on error
        this.statusService.updateStatus({
          isLoading: false,
          progress: 100,
          storeId: 'areas',
          itemId: areaId,
          projectId: effectiveProjectId,
          operation: 'websocket-update'
        });

        return of(void 0);
      })
    );
  }
}
